import React, { Suspense, lazy } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import { SvgImage } from 'components/SvgImage'
import { WellnessFooter } from 'components/WellnessFooter'

import { selectLanguage } from 'root-redux/selects/common'

import { useExperimentalFeatures } from 'hooks/useExperimentalFeatures'
import { useGetPageInfo } from 'hooks/useGetPageInfo'
import { useGetSubscriptions } from 'hooks/useGetSubscriptions'
import { useNextStep } from 'hooks/useNextStep'
import { useScrollEvent } from 'hooks/useScrollEvent'
import { useTrackPageScrollDepth } from 'hooks/useTrackPageScrollDepth'

import { getButtonTapEventData } from 'helpers/getButtonTapEventData'
import { getLocalizedPrice } from 'helpers/getLocalizedPrice'

import { getCurrencyLabel } from 'modules/purchase/helpers/getCurrencyLabel'
import {
  usePurchaseStore,
  useRoundedSubscriptionTags,
} from 'modules/purchase/hooks'
import { LONG_PAYWALL_TRACKED_BREAKPOINTS } from 'modules/purchase/pages/Purchase/shared/constants'

import { TPageProps } from 'models/common.model'

import { eventLogger } from 'services/eventLogger.service'

import checkIcon from 'assets/images/sprite/green-circle-checkbox.svg'

import {
  CDN_FOLDER_LINK,
  DiscountPercent,
  IMAGES_PATH,
  Language,
  PageVersions,
  ScreenName,
  SubscriptionListType,
  SubscriptionTagsType,
} from 'root-constants'

import { StyledLandingWellness as S } from './LandingWellness.styles'
import { BeforeYouBuy } from './components/BeforeYouBuy'
import { CarouselSkeleton } from './components/CarouselSkeleton'
import { EarlySigns } from './components/EarlySigns'
import { GoodNews } from './components/GoodNews'
import { IgnoredSigns } from './components/IgnoredSigns'
import { TrustBadge } from './components/TrustBadge'
import {
  LANDING_WELLNESS_CONTENT_BY_VERSION,
  LANDING_WELLNESS_FEATURES,
  LANDING_WELLNESS_GUARANTEE_BLOCKS,
  LANDING_WELLNESS_OUTCOMES,
} from './constants'

const DeskbookPreview = lazy(() =>
  import('./components/DeskbookPreview').then((m) => ({
    default: m.DeskbookPreview,
  })),
)
const Ingredients = lazy(() =>
  import('./components/Ingredients').then((m) => ({ default: m.Ingredients })),
)
const WellnessReviews = lazy(() =>
  import('./components/WellnessReviews').then((m) => ({
    default: m.WellnessReviews,
  })),
)

export const LandingWellness: React.FC<TPageProps> = ({
  pageId,
  nextPagePath,
  isFirstPage,
}) => {
  const { t } = useTranslation()
  const { search } = useLocation()
  const language = useSelector(selectLanguage)
  const { currentSubscriptionPageId } = useGetPageInfo()
  const { scrollEvent } = useScrollEvent(ScreenName.LANDING_WELLNESS)
  const { isAyah95WldbFirstScreenV2Test } = useExperimentalFeatures()
  const { selectedSubscription, currency } = usePurchaseStore()
  const subscriptionTags = useRoundedSubscriptionTags(
    `${SubscriptionTagsType.NO_TAX},${SubscriptionTagsType.GUIDE_WELLNESS}`,
  )
  const pageVersion = isAyah95WldbFirstScreenV2Test
    ? PageVersions.V2
    : PageVersions.V1
  const {
    bannerKey,
    titleKey: pageTitleKey,
    subtitleKey,
    ingredientsTitleKey,
    benefits,
    heroImage,
  } = LANDING_WELLNESS_CONTENT_BY_VERSION[pageVersion]

  useTrackPageScrollDepth({
    trackedBreakpoints: LONG_PAYWALL_TRACKED_BREAKPOINTS,
    eventCallback: scrollEvent,
  })

  useGetSubscriptions({
    enabled: isAyah95WldbFirstScreenV2Test,
    subscriptionType: SubscriptionListType.PURCHASE,
    tags: subscriptionTags,
  })

  const moneyBackImage = `${CDN_FOLDER_LINK}${IMAGES_PATH}/money-back_${language}.png`

  const currencyLabel = getCurrencyLabel(currency)

  const handleContinue = useNextStep({
    pageId,
    question: t('onboarding.landingWellness.title', { lng: Language.EN }),
    nextPagePath: nextPagePath || `${currentSubscriptionPageId}${search}`,
    isFirstPage,
  })

  const handleCtaClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    const { buttonText, buttonOrder } = getButtonTapEventData(event)

    eventLogger.logPlanPageButtonClicked({
      buttonText,
      buttonNumber: buttonOrder,
      screenName: ScreenName.LANDING_WELLNESS,
    })

    handleContinue('')
  }

  const handleImageClick = () => {
    eventLogger.logPlanPageButtonClicked({
      buttonText: 'image-deskbook',
      buttonNumber: 0,
      screenName: ScreenName.LANDING_WELLNESS,
    })

    handleContinue('')
  }

  return (
    <S.Wrapper>
      <S.DiscountBanner>
        <S.DiscountBannerText>
          <Trans
            i18nKey={bannerKey}
            values={{ discount: DiscountPercent.SIXTY }}
          />
        </S.DiscountBannerText>
      </S.DiscountBanner>

      <S.Content>
        <S.Intro>
          {isAyah95WldbFirstScreenV2Test ? (
            <S.HeroButton
              type="button"
              aria-label={t('actions.buyNow')}
              onClick={handleImageClick}
            >
              <S.DeskbookImage
                src={heroImage}
                alt="The Islamic Wellness Deskbook"
              />
            </S.HeroButton>
          ) : (
            <S.DeskbookImage
              src={heroImage}
              alt="The Islamic Wellness Deskbook"
            />
          )}

          <S.Heading>
            {pageVersion === PageVersions.V1 && <TrustBadge />}

            <S.Title>
              {t(pageTitleKey, {
                price: selectedSubscription
                  ? t('purchaseIntroOffer.subscription.price', {
                      currency: currencyLabel,
                      price: getLocalizedPrice(
                        selectedSubscription.trialPrices.fullPrice,
                        currency,
                      ),
                    })
                  : undefined,
              })}
            </S.Title>
            <S.Subtitle>{t(subtitleKey)}</S.Subtitle>
          </S.Heading>

          <S.Benefits>
            {benefits.map(({ titleKey, descriptionKey }) => (
              <S.Benefit key={titleKey}>
                <S.ListItemIcon svg={checkIcon} width={24} height={24} />

                <S.ListItemContent>
                  <S.ListItemTitle>{t(titleKey)}</S.ListItemTitle>

                  {descriptionKey && (
                    <S.ListItemDescription>
                      {t(descriptionKey)}
                    </S.ListItemDescription>
                  )}
                </S.ListItemContent>
              </S.Benefit>
            ))}
          </S.Benefits>

          <S.Guarantee>
            <S.GuaranteeBadge src={moneyBackImage} alt="Money back image" />

            {LANDING_WELLNESS_GUARANTEE_BLOCKS.map(
              ({ titleKey, descriptionKey }) => (
                <S.GuaranteeBlock key={titleKey}>
                  <S.BlockTitle>{t(titleKey)}</S.BlockTitle>
                  <S.ListItemDescription>
                    {t(descriptionKey)}
                  </S.ListItemDescription>
                </S.GuaranteeBlock>
              ),
            )}
          </S.Guarantee>
        </S.Intro>

        <Suspense fallback={<CarouselSkeleton height={448} dotsCount={4} />}>
          <DeskbookPreview />
        </Suspense>

        <S.Features>
          {LANDING_WELLNESS_FEATURES.map(({ titleKey, descriptionKey }) => (
            <S.Feature key={titleKey}>
              <S.ListItemIcon svg={checkIcon} width={24} height={24} />

              <S.ListItemContent>
                <S.BlockTitle>{t(titleKey)}</S.BlockTitle>
                <S.FeatureDescription>{t(descriptionKey)}</S.FeatureDescription>
              </S.ListItemContent>
            </S.Feature>
          ))}
        </S.Features>

        <S.Outcomes>
          <S.OutcomesTitle>
            {t('onboarding.landingWellness.whatYouGet.title')}
          </S.OutcomesTitle>

          <S.OutcomesList>
            {LANDING_WELLNESS_OUTCOMES.map(
              ({ icon, titleKey, descriptionKey }) => (
                <S.Outcome key={titleKey}>
                  <S.OutcomeIconBadge>
                    <SvgImage svg={icon} width={20} height={20} />
                  </S.OutcomeIconBadge>

                  <S.ListItemContent>
                    <S.ListItemTitle>{t(titleKey)}</S.ListItemTitle>
                    <S.OutcomeDescription>
                      {t(descriptionKey)}
                    </S.OutcomeDescription>
                  </S.ListItemContent>
                </S.Outcome>
              ),
            )}
          </S.OutcomesList>
        </S.Outcomes>

        <Suspense fallback={<CarouselSkeleton height={602} dotsCount={5} />}>
          <Ingredients titleKey={ingredientsTitleKey} />
        </Suspense>

        <EarlySigns />

        <IgnoredSigns />

        <GoodNews />

        <Suspense fallback={<CarouselSkeleton height={440} dotsCount={3} />}>
          <WellnessReviews />
        </Suspense>

        <BeforeYouBuy />

        <S.StickyCtaSection>
          <S.Button
            type="button"
            data-order={1}
            $isUppercase={pageVersion === PageVersions.V2}
            onClick={handleCtaClick}
          >
            {t('actions.buyNow')}
          </S.Button>
        </S.StickyCtaSection>
      </S.Content>

      <WellnessFooter />
    </S.Wrapper>
  )
}
